import type { I18n, MessageDescriptor } from "@lingui/core";

import { msg } from "@lingui/core/macro";
import { Trans, useLingui } from "@lingui/react/macro";
import { FileText, Info, LineChart } from "lucide-react";
import { observer } from "mobx-react-lite";
import { type ReactNode } from "react";

import { cn } from "@/utils/shadcn-ui";

import type { MeasurementColumnSpec } from "./measurement-columns";
import type { MeasurementRow, Measurements } from "./measurements";

import {
  type CodeListCell,
  type MeasurementCell,
  type MeasurementSession,
  QualityLevel,
  SessionKind,
  type ThresholdInfo,
  type ValueCell,
} from "../../../api";
import { QuickLookButton } from "../../../components/quick-look/quick-look-button";
import { Tooltip } from "../../../components/ui-kit/base/tooltip";
import { Button } from "../../../components/ui-kit/button";
import { ppgConditionText, ppgQuality } from "../../../constants/enum-to-text";

export const EM_DASH = "—";

/** The position names the API uses for the three clinical conditions. */
const POSITIONS: Record<string, MessageDescriptor> = {
  standing: msg`standing`,
  supine: msg`supine`,
  sitting: msg`sitting`,
};

/** Named HR algorithms and AF models, as the selectors offer them. */
export const VARIANT_LABELS: Record<string, string> = {
  qppg_beats: "QPPG",
  emd: "EMD",
  device: "Device",
  logreg: "LogReg",
  lgbm: "LGBM",
};

/**
 * The Date column: the day on a day row; on a session sub-row, its time and —
 * for anything but a full measurement — what kind of session it is. A day row
 * wears its primary session's marker too, so a partial or standalone-only day
 * says so without expanding.
 */
export function DateCell({ row }: { row: MeasurementRow }) {
  const { i18n } = useLingui();
  const session = row.session;
  const label = row.isSession
    ? new Intl.DateTimeFormat(i18n.locale, {
        hour: "2-digit",
        minute: "2-digit",
      }).format(new Date(session.session_start))
    : new Intl.DateTimeFormat(i18n.locale, {
        weekday: "short",
        day: "numeric",
        month: "short",
        year: "numeric",
        timeZone: "UTC",
      }).format(new Date(`${row.date}T00:00:00Z`));

  return (
    <div className={row.isSession ? "pl-3 text-left" : "text-left"}>
      <div className={row.isSession ? undefined : "font-medium"}>{label}</div>
      <SessionMarker session={session} />
    </div>
  );
}

/**
 * A standalone reading is marked by its position and never as incomplete: it
 * wasn't trying to be a full measurement, so nothing is missing from it.
 */
function SessionMarker({ session }: { session: MeasurementSession }) {
  const { i18n } = useLingui();
  if (session.kind === SessionKind.standalone) {
    const position = positionText(session.position, i18n);
    return (
      <div className="text-xs text-muted-foreground">
        <Trans>{position} · standalone</Trans>
      </div>
    );
  }
  if (session.kind === SessionKind.partial) {
    const missing = (session.missing_items ?? []).map((item) =>
      ppgConditionText(item),
    );
    if (missing.length === 0) {
      return (
        <div className="text-xs text-muted-foreground">
          <Trans>Incomplete</Trans>
        </div>
      );
    }
    const count = missing.length;
    // The count keeps the marker to one line; the items are a hover away.
    return (
      <Tooltip content={missing.join(", ")}>
        <div className="inline-flex items-center gap-1 text-xs text-muted-foreground">
          <Trans>Incomplete · {count} missing</Trans>
          <Info size={12} aria-hidden />
        </div>
      </Tooltip>
    );
  }
  return null;
}

export function positionText(
  position: string | null | undefined,
  i18n: I18n,
): string {
  if (!position) return "";
  const known = POSITIONS[position];
  // A non-conforming historical condition is shown raw, never guessed.
  return known ? i18n._(known) : position;
}

/** A plain value cell: DRI, dLVFP, weight, a symptom, … */
export function ValueCellView({
  cell,
  spec,
}: {
  cell: MeasurementCell | null | undefined;
  spec: MeasurementColumnSpec;
}) {
  return isValueCell(cell) ? (
    <FormattedValue cell={cell} spec={spec} />
  ) : (
    <>{EM_DASH}</>
  );
}

function FormattedValue({
  cell,
  spec,
}: {
  cell: ValueCell;
  spec: MeasurementColumnSpec;
}) {
  const { i18n } = useLingui();
  return (
    <ThresholdMark threshold={cell.threshold}>
      {formatMeasurement(cell.value, spec, i18n.locale)}
    </ThresholdMark>
  );
}

/**
 * A value that alerted the clinician — from the persisted alerts, so it means
 * exactly what the Alerts tab means, not "abnormal". Columns without
 * threshold flagging (AF probability, rhythm, perceived health change) carry no
 * threshold at all, so they never get here with one.
 */
function ThresholdMark({
  threshold,
  children,
}: {
  threshold: ThresholdInfo | null | undefined;
  children: ReactNode;
}) {
  const { t } = useLingui();
  if (!threshold?.crossed) return <>{children}</>;
  // Without VIEW_ALERTS only `crossed` comes through.
  const reason = threshold.reason ?? t`Crossed an alert threshold`;
  const content =
    threshold.seen == null
      ? reason
      : threshold.seen
        ? t`${reason} · seen`
        : t`${reason} · unseen`;
  return (
    <Tooltip content={content}>
      <span
        data-threshold-crossed
        className="rounded-sm bg-danger-soft px-1 py-0.5 font-semibold text-danger-foreground"
      >
        {children}
      </span>
    </Tooltip>
  );
}

/** Blood pressure as `sys/dia`; each side stands, and alerts, on its own. */
export function BloodPressureCell({
  cell,
  spec,
}: {
  cell: MeasurementCell | null | undefined;
  spec: MeasurementColumnSpec;
}) {
  const { i18n } = useLingui();
  const parts = cell && "parts" in cell ? cell.parts : undefined;
  if (!parts) return <>{EM_DASH}</>;
  const side = (key: "systolic" | "diastolic") => {
    const part = parts[key];
    return part ? (
      <ThresholdMark threshold={part.threshold}>
        {formatMeasurement(part.value, spec, i18n.locale)}
      </ThresholdMark>
    ) : (
      EM_DASH
    );
  };
  return (
    <>
      {side("systolic")}/{side("diastolic")}
    </>
  );
}

/** HR, SpO2 or AF probability, read from the session's signals. */
export const SignalValueCell = observer(function SignalValueCell({
  model,
  session,
  spec,
}: {
  model: Measurements;
  session: MeasurementSession;
  spec: MeasurementColumnSpec & { key: "hr" | "spo2" | "af_probability" };
}) {
  const cell = model.signalValue(session, spec.key);
  return cell ? <FormattedValue cell={cell} spec={spec} /> : <>{EM_DASH}</>;
});

/**
 * The session's PPG quality as a dot. The API grades the flag (`ok` / `warn` /
 * `bad`, total over the enum); the palette is ours. The flag itself is in the
 * tooltip of an amber or red dot, since several flags share each of those.
 */
export function QualityCell({ session }: { session: MeasurementSession }) {
  const { i18n } = useLingui();
  const quality = session.quality?.ppg;
  if (!quality) return <>{EM_DASH}</>;
  // A flag newer than this build falls back to the API's own name for it.
  const flagLabel = (ppgQuality as Partial<Record<number, MessageDescriptor>>)[
    quality.flag
  ];
  const flag = flagLabel ? i18n._(flagLabel) : quality.name;
  // Green is always "OK", which the legend already says; amber and red each
  // cover several flags, so there the tooltip names which one it is.
  return (
    <Tooltip content={quality.level === QualityLevel.ok ? null : flag}>
      <span
        data-quality-level={quality.level}
        aria-label={flag}
        className="inline-flex items-center"
      >
        <QualityDot level={quality.level} />
      </span>
    </Tooltip>
  );
}

export function QualityDot({ level }: { level: QualityLevel }) {
  return (
    <span className={cn("inline-block size-2.5 rounded-full", DOT[level])} />
  );
}

/**
 * Explains the dot colours — the table carries no other key for them. Shown
 * from the info icon on the Quality column's header.
 */
export function QualityLegend() {
  return (
    <div className="flex flex-col gap-1 text-xs">
      <span className="inline-flex items-center gap-1.5">
        <QualityDot level={QualityLevel.ok} />
        <Trans>Signal OK</Trans>
      </span>
      <span className="inline-flex items-center gap-1.5">
        <QualityDot level={QualityLevel.warn} />
        <Trans>Check the signal</Trans>
      </span>
      <span className="inline-flex items-center gap-1.5">
        <QualityDot level={QualityLevel.bad} />
        <Trans>Unusable</Trans>
      </span>
    </div>
  );
}

const DOT: Record<QualityLevel, string> = {
  [QualityLevel.ok]: "bg-success-solid",
  [QualityLevel.warn]: "bg-warning-solid",
  [QualityLevel.bad]: "bg-danger-solid",
};

export function RhythmCell({
  cell,
}: {
  cell: MeasurementCell | null | undefined;
}) {
  const codes =
    cell && "value" in cell && Array.isArray(cell.value)
      ? (cell as CodeListCell).value
      : [];
  return <>{codes.length > 0 ? codes.join(", ") : EM_DASH}</>;
}

export const QuickLookCell = observer(function QuickLookCell({
  model,
  row,
}: {
  model: Measurements;
  row: MeasurementRow;
}) {
  const { t } = useLingui();
  const quickLook = model.quickLookFor(row);
  if (!quickLook) return <>{EM_DASH}</>;
  return (
    <QuickLookButton
      size="small"
      label={t`QuickLook`}
      resultLabel={t`View QuickLook`}
      model={quickLook}
    />
  );
});

/**
 * The day's ECG PDF. The upload is best-effort, so without a report the
 * interactive plot of the recording stands in, for users allowed to see it.
 */
export function EcgActionCell({
  session,
  downloadBase,
  onOpenPlot,
}: {
  session: MeasurementSession;
  downloadBase: string;
  onOpenPlot: ((ecgUuid: string) => void) | null;
}) {
  const { t } = useLingui();
  const action = session.actions.open_ecg;
  if (action?.report_uuid) {
    return (
      <a
        href={`${downloadBase}/medical-reports/download/${action.report_uuid}`}
        target="_blank"
        rel="noreferrer"
        aria-label={t`Open ECG report`}
        className="inline-flex text-brand-primary"
      >
        <FileText size={16} />
      </a>
    );
  }
  if (action?.ecg_uuid && onOpenPlot) {
    const ecgUuid = action.ecg_uuid;
    return (
      <Button
        variant="tertiary"
        size="sm"
        icon={LineChart}
        label={t`Open ECG plot`}
        onClick={() => onOpenPlot(ecgUuid)}
      />
    );
  }
  return <>{EM_DASH}</>;
}

function isValueCell(
  cell: MeasurementCell | null | undefined,
): cell is ValueCell {
  return !!cell && !("parts" in cell) && !Array.isArray(cell.value);
}

/** A value as the table shows it: the column's decimals and scale, locale-aware. */
export function formatMeasurement(
  value: number | null | undefined,
  spec: MeasurementColumnSpec,
  locale: string,
): string {
  if (value == null) return EM_DASH;
  const decimals = spec.decimals ?? 0;
  return new Intl.NumberFormat(locale, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(value * (spec.scale ?? 1));
}
